// src/useKegOpsState.js
// ============================================================
// CENTRALIZED APPLICATION STATE MANAGEMENT
// ============================================================
// This hook manages ALL app state. To add new state:
// 1. Add state variable and setter in appropriate section
// 2. Add to return object
// 3. Document what it's used for in the comment above the section
//
// SECTIONS:
// - VIEW STATE: Navigation and current view context
// - LOADING STATE: Data loading and error states
// - SETTINGS: App-wide configuration
// - ACCOUNT: User profile and permissions
// - THEME: Dark/light mode preference
// - DATA: Core application data (kegs, locations)
// - UI FILTERS: Delegated to useFilters hook
// - MODAL VISIBILITY: show/hide booleans for each modal
// - FORM STATES: Form data for all modals
// ============================================================

import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { KEG_FORM_DEFAULTS, SETTINGS_DEFAULTS, ACCOUNT_DEFAULTS } from './constants/formDefaults';
import * as SupabaseService from './supabaseService';
import * as BreweryService from './services/breweryService';
import { useFilters } from './hooks/useFilters';

export const useKegOpsState = () => {
  // ============================================
  // VIEW STATE
  // ============================================
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedKegId, setSelectedKegId] = useState(null);
  const [dashboardTab, setDashboardTab] = useState('kegs');
  
  // ============================================
  // LOADING STATE
  // ============================================
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  
  // ============================================
  // SETTINGS
  // ============================================
  const [settings, setSettings] = useState(SETTINGS_DEFAULTS);

  // ============================================
  // ACCOUNT
  // ============================================
  const [account, setAccount] = useState(ACCOUNT_DEFAULTS);

  // ============================================
  // THEME
  // ============================================
  // Initialize from localStorage or default to 'system'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('kegops-theme');
    return (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) ? savedTheme : 'system';
  });

  // ============================================
  // DATA
  // ============================================
  const [kegs, setKegs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [breweries, setBreweries] = useState([]); // User's brewery memberships
  // Persist last selected brewery in localStorage
  const [selectedBreweryId, setSelectedBreweryId] = useState(() => {
    return localStorage.getItem('kegops_last_brewery') || null;
  });

  // ============================================
  // UI FILTERS (delegated to useFilters hook)
  // ============================================
  const filters = useFilters();
  
  // ============================================
  // MODAL VISIBILITY STATES
  // ============================================
  const [showKegModal, setShowKegModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showCleanModal, setShowCleanModal] = useState(false);
  const [showFillModal, setShowFillModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showTapNumberModal, setShowTapNumberModal] = useState(false);
  const [tapModalKegId, setTapModalKegId] = useState(null);
  const [pendingTapStatus, setPendingTapStatus] = useState(null);
  const [showTapSelectionModal, setShowTapSelectionModal] = useState(false);
  const [tapSelectionKeg, setTapSelectionKeg] = useState(null);
  const [selectedTapLocation, setSelectedTapLocation] = useState('');
  const [selectedTapSublocation, setSelectedTapSublocation] = useState('');
  const [selectedTapName, setSelectedTapName] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showSublocationModal, setShowSublocationModal] = useState(false);
  
  // ============================================
  // ONBOARDING (First brewery creation)
  // ============================================
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCreatingFirstBrewery, setIsCreatingFirstBrewery] = useState(false);
  
  // ============================================
  // FORM STATES
  // ============================================
  const [editingKeg, setEditingKeg] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingSublocation, setEditingSublocation] = useState(null);
  const [selectedMaintenanceKeg, setSelectedMaintenanceKeg] = useState(null);
  const [selectedRatingKeg, setSelectedRatingKeg] = useState(null);
  const [selectedHistoryBeer, setSelectedHistoryBeer] = useState(null);
  const [newLocation, setNewLocation] = useState({ name: '', description: '' });
  const [newSublocation, setNewSublocation] = useState({ name: '', description: '', parentId: null });
  const [styleSearch, setStyleSearch] = useState("");
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const [beerImageFile, setBeerImageFile] = useState(null);
  const [beerImagePreview, setBeerImagePreview] = useState(null);
  
  const [kegForm, setKegForm] = useState(KEG_FORM_DEFAULTS);
  
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'Viewer' });
  const [maintenanceForm, setMaintenanceForm] = useState({
    date: new Date().toISOString().split('T')[0], items: []
  });
  const [qrSettings, setQrSettings] = useState({
    showKegNumber: true, showTareWeight: false, showFullWeight: false
  });
  const [ratingForm, setRatingForm] = useState({ rating: 0, notes: '', raterName: '' });
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [feedbackName, setFeedbackName] = useState("");
  const [newBeer, setNewBeer] = useState({
    name: "", style: "", abv: "", ibu: "", og: "", fg: "", description: "", imageUrl: null, brewery: '', location: ''
  });
  const [isEditingBeer, setIsEditingBeer] = useState(false);

  // ============================================
  // LOAD DATA WHEN AUTH IS READY
  // ============================================
  const hasLoadedData = useRef(false);

  // ============================================
  // AUTH SESSION MANAGEMENT (Supabase Auth)
  // ============================================
  useEffect(() => {
    const MAX_SESSION_AGE = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    const SESSION_STORAGE_KEY = 'kegops_session_v1';

    const persistSession = (session) => {
      if (!session?.access_token || !session?.refresh_token) return;
      const payload = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        login_time: Date.now(),
        expires_at: session.expires_at
      };
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
    };

    const clearPersistedSession = () => {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      localStorage.removeItem('kegops_login_time');
    };

    const restorePersistedSession = async () => {
      const raw = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return false;
      try {
        const saved = JSON.parse(raw);
        if (!saved.access_token || !saved.refresh_token || !saved.login_time) {
          clearPersistedSession();
          return false;
        }
        if (Date.now() - saved.login_time > MAX_SESSION_AGE) {
          console.log('🕐 Stored session exceeded max age - clearing');
          clearPersistedSession();
          await supabase.auth.signOut();
          return false;
        }
        const { data, error } = await supabase.auth.setSession({
          access_token: saved.access_token,
          refresh_token: saved.refresh_token
        });
        if (error) {
          console.warn('⚠️ Failed to restore session from storage:', error.message);
          clearPersistedSession();
          return false;
        }
        console.log('✅ Restored session from storage for user:', data?.session?.user?.id || 'unknown');
        // Re-set login_time to now to refresh age window
        localStorage.setItem('kegops_login_time', Date.now().toString());
        return !!data.session;
      } catch (e) {
        clearPersistedSession();
        return false;
      }
    };

    // Check session age on app load
    const checkSessionAge = async () => {
      const loginTime = localStorage.getItem('kegops_login_time');
      
      if (loginTime && Date.now() - parseInt(loginTime) > MAX_SESSION_AGE) {
        // Session expired - sign out
        console.log('🕐 Session expired after 30 days - signing out');
        await supabase.auth.signOut();
        localStorage.removeItem('kegops_login_time');
        return false;
      }
      return true;
    };

    // Initialize current session on mount
    checkSessionAge().then((sessionValid) => {
      if (!sessionValid) {
        setIsAuthChecked(true);
        setIsLoading(false); // No valid session, stop loading
        return;
      }
      
      // Attempt manual restore from stored tokens (helps localhost refresh cases)
      restorePersistedSession()
        .finally(() => {
          supabase.auth.getSession()
            .then(({ data }) => {
              const session = data?.session || null;
              console.log('ℹ️ getSession on load. Session present:', !!session, 'user:', session?.user?.id || 'none');
              setAccount(prev => ({ ...prev, session, user: session?.user || null }));

              // Mark auth as checked immediately after session resolution
              setIsAuthChecked(true);

              if (session?.user?.id) {
                console.log('👤 Your User ID:', session.user.id);
                // Fire-and-forget loading so auth gate can clear even if data takes time
                loadUserBreweries(session.user.id);
                if (!hasLoadedData.current) {
                  hasLoadedData.current = true;
                  loadAllData();
                }
              } else {
                // No user session - stop loading so Login can render
                setIsLoading(false);
              }
            })
            .catch(() => {
              // If getSession fails, still allow UI to render login
              setIsLoading(false);
              setIsAuthChecked(true);
            });
        });
    });

    // Subscribe to auth state changes
    const { data: authSub } = supabase.auth.onAuthStateChange((event, session) => {
      setAccount(prev => ({ ...prev, session, user: session?.user || null }));
      
      // Store login time when user signs in
      if (event === 'SIGNED_IN' && session?.user?.id) {
        const loginTime = Date.now();
        localStorage.setItem('kegops_login_time', loginTime.toString());
        console.log('🔐 Login time stored:', new Date(loginTime).toLocaleString());
        persistSession(session);
        
        // Load breweries and data with proper awaiting and retry on empty results
        (async () => {
          // Try loading breweries; if none found yet, retry a few times to wait for trigger inserts
          const maxRetries = 3;
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            await loadUserBreweries(session.user.id);
            const currentBreweries = (breweries && breweries.length) ? breweries.length : 0;
            console.log(`🔁 Attempt ${attempt + 1}/${maxRetries} - breweries found:`, currentBreweries);
            if (currentBreweries > 0) break;
            // small backoff before retry
            await new Promise(res => setTimeout(res, 500 * (attempt + 1)));
          }

          if (!hasLoadedData.current) {
            hasLoadedData.current = true;
            await loadAllData();
          }
          
          // Check for pending invite token after login
          const pendingToken = sessionStorage.getItem('pendingInviteToken');
          if (pendingToken) {
            console.log('🔗 Found pending invite token, redirecting to accept...');
            sessionStorage.removeItem('pendingInviteToken');
            setTimeout(() => {
              window.location.href = `/accept-invite?token=${pendingToken}`;
            }, 1000);
          }
        })();
      }
      
      // Refresh token updates - keep session fresh locally
      if (event === 'TOKEN_REFRESHED' && session?.user?.id) {
        persistSession(session);
      }
      
      // Clear login time when user signs out
      if (event === 'SIGNED_OUT') {
        clearPersistedSession();
      }
      
      // Load breweries for existing sessions
      if (session?.user?.id && event !== 'SIGNED_IN') {
        loadUserBreweries(session.user.id);
      }
    });

    return () => {
      try { authSub.subscription.unsubscribe(); } catch (_) {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('📋 [loadAllData] Starting data load...');
      // Fetch all data in parallel for faster loading
      const [settingsResult, accountResult, locationsResult, kegsResult] = await Promise.all([
        SupabaseService.fetchSettings(),
        SupabaseService.fetchAccount(),
        SupabaseService.fetchLocations(),
        SupabaseService.fetchKegs(selectedBreweryId)
      ]);

      console.log('📋 [loadAllData] Results:', { 
        settingsResult: { success: settingsResult?.success, error: settingsResult?.error },
        accountResult: { success: accountResult?.success, error: accountResult?.error },
        locationsResult: { success: locationsResult?.success, count: locationsResult?.data?.length },
        kegsResult: { success: kegsResult?.success, count: kegsResult?.data?.length }
      });

      // Process settings
      if (settingsResult && settingsResult.success !== false) {
        console.log('✅ [loadAllData] Settings loaded');
        console.log('📊 [loadAllData] Settings content:', settingsResult);
        setSettings(settingsResult);
      } else if (settingsResult && settingsResult.error) {
        console.error('❌ [loadAllData] Settings error:', settingsResult.error);
      }
      
      // Process account
      if (accountResult && accountResult.success !== false) {
        console.log('✅ [loadAllData] Account loaded');
        console.log('📊 [loadAllData] Account content:', accountResult);
        // Preserve existing session/user while updating account profile
        setAccount(prev => ({ ...prev, ...accountResult }));
      } else if (accountResult && accountResult.error) {
        console.error('❌ [loadAllData] Account error:', accountResult.error);
        if (accountResult.error.includes('Failed to fetch') || accountResult.error.includes('ECONNREFUSED')) {
          throw new Error('Cannot connect to Supabase. Please check your environment variables.');
        }
      }
      
      // Process locations
      if (locationsResult && locationsResult.success !== false && Array.isArray(locationsResult)) {
        setLocations(locationsResult);
      } else if (locationsResult && locationsResult.error) {
        console.error('Locations error:', locationsResult.error);
      }
      
      // Process kegs
      if (kegsResult && kegsResult.success !== false && Array.isArray(kegsResult)) {
        setKegs(kegsResult);
      } else if (kegsResult && kegsResult.error) {
        console.error('Kegs error:', kegsResult.error);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data. Please check your Supabase configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load user's brewery memberships when they log in
   * Checks for recent invite acceptance to avoid showing onboarding incorrectly
   */
  const loadUserBreweries = async (userId) => {
    if (!userId) return;
    console.log('📦 Loading breweries for user:', userId);
    const result = await BreweryService.getUserBreweries(userId);
    console.log('📦 Brewery fetch result:', { success: result.success, count: result.data?.length || 0, data: result.data });
    if (result.success) {
      setBreweries(result.data);
      // Try to restore last selected brewery from localStorage
      const lastBreweryId = localStorage.getItem('kegops_last_brewery');
      const found = result.data.find(b => b.id === lastBreweryId);
      if (found) {
        setSelectedBreweryId(found.id);
      } else if (result.data.length > 0) {
        setSelectedBreweryId(result.data[0].id);
      }
      
      // Don't show onboarding if user just accepted an invite (check sessionStorage flag)
      const justAcceptedInvite = sessionStorage.getItem('justAcceptedInvite');
      if (justAcceptedInvite) {
        sessionStorage.removeItem('justAcceptedInvite');
        console.log('📦 User just accepted invite, skipping onboarding check');
        setShowOnboarding(false);
      } else {
        const shouldShowOnboarding = result.data.length === 0;
        console.log('📦 Setting showOnboarding to:', shouldShowOnboarding);
        setShowOnboarding(shouldShowOnboarding);
      }
    } else {
      console.error('❌ Error loading breweries:', result.error);
    }
  };

  // Save selected brewery to localStorage whenever it changes
  useEffect(() => {
    if (selectedBreweryId) {
      localStorage.setItem('kegops_last_brewery', selectedBreweryId);
      // Reload kegs when brewery changes
      console.log('🔄 Brewery changed to:', selectedBreweryId, '- reloading kegs');
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBreweryId]);

  /**
   * Complete onboarding by creating first free brewery
   */
  const completeOnboarding = async (breweryName) => {
    setIsCreatingFirstBrewery(true);
    const result = await BreweryService.createFirstFreeBrewery(account.user.id, breweryName);
    
    if (result.success) {
      // Add new brewery to breweries list
      const newBrewery = {
        ...result.data,
        userRole: 'bar_owner',
        membershipId: undefined
      };
      setBreweries(prev => [...prev, newBrewery]);
      setSelectedBreweryId(result.data.id);
      setShowOnboarding(false);
      setCurrentView('dashboard');
    } else {
      console.error('Onboarding error:', result.error);
    }
    
    setIsCreatingFirstBrewery(false);
  };

  // Return all state and setters
  return {
    // View state
    currentView, setCurrentView,
    selectedKegId, setSelectedKegId,
    dashboardTab, setDashboardTab,
    
    // Loading state
    isLoading, setIsLoading,
    error, setError,
    isAuthChecked,
    
    // Settings
    settings, setSettings,
    
    // Account
    account, setAccount,
    
    // Data
    kegs, setKegs,
    locations, setLocations,
    breweries, setBreweries,
    selectedBreweryId, setSelectedBreweryId,
    
    // UI filters (spread from useFilters hook)
    ...filters,
    
    // Modal visibility
    showKegModal, setShowKegModal,
    showQRModal, setShowQRModal,
    showMaintenanceModal, setShowMaintenanceModal,
    showRatingModal, setShowRatingModal,
    showFeedbackModal, setShowFeedbackModal,
    showCleanModal, setShowCleanModal,
    showFillModal, setShowFillModal,
    showHistoryModal, setShowHistoryModal,
    showUserModal, setShowUserModal,
    showLocationModal, setShowLocationModal,
    showSublocationModal, setShowSublocationModal,
    
    // Onboarding
    showOnboarding, setShowOnboarding,
    isCreatingFirstBrewery,
    completeOnboarding,
    
    // Form states
    editingKeg, setEditingKeg,
    editingUser, setEditingUser,
    editingLocation, setEditingLocation,
    editingSublocation, setEditingSublocation,
    selectedMaintenanceKeg, setSelectedMaintenanceKeg,
    selectedRatingKeg, setSelectedRatingKeg,
    selectedHistoryBeer, setSelectedHistoryBeer,
    newLocation, setNewLocation,
    newSublocation, setNewSublocation,
    styleSearch, setStyleSearch,
    showStyleDropdown, setShowStyleDropdown,
    beerImageFile, setBeerImageFile,
    beerImagePreview, setBeerImagePreview,
    kegForm, setKegForm,
    userForm, setUserForm,
    maintenanceForm, setMaintenanceForm,
    qrSettings, setQrSettings,
    ratingForm, setRatingForm,
    feedbackRating, setFeedbackRating,
    feedbackNotes, setFeedbackNotes,
    feedbackName, setFeedbackName,
    newBeer, setNewBeer,
    isEditingBeer, setIsEditingBeer,
    showTapNumberModal, setShowTapNumberModal,
    tapModalKegId, setTapModalKegId,
    pendingTapStatus, setPendingTapStatus,
    showTapSelectionModal, setShowTapSelectionModal,
    tapSelectionKeg, setTapSelectionKeg,
    selectedTapLocation, setSelectedTapLocation,
    selectedTapSublocation, setSelectedTapSublocation,
    selectedTapName, setSelectedTapName,
    
    // Theme
    theme, setTheme,
    
    // Data loaders
    loadAllData,
    loadUserBreweries
  };
};